import React, { useState } from 'react';
import {
  ShieldCheck,
  UserCheck,
  Lock,
  Database,
  Ban,
  MailCheck,
  CreditCard,
  Coins,
  EyeOff,
  UserX,
  Activity,
  Award,
  ChevronRight,
  CheckCircle2,
  Sparkles,
  Search,
  Scale,
  RefreshCw,
  HeartHandshake,
  Building2,
  User
} from 'lucide-react';
import { LandingHeader } from './LandingHeader';
import LandingFooter from './LandingFooter';

interface TrustSignalCard {
  id: string;
  title: string;
  description: string;
  icon: React.ElementType;
}

interface CommitmentSection {
  id: string;
  title: string;
  icon: React.ElementType;
  summary: string;
  points?: string[];
}

// 12 Visual Trust Signal Badges / Cards at the top of the page
const trustSignals: TrustSignalCard[] = [
  {
    id: 'badge-recruiter-verif',
    title: 'Recruiter Verification Required',
    description: 'Every corporate recruiter undergoes company domain checks and organizational review before accessing search.',
    icon: Building2,
  },
  {
    id: 'badge-candidate-review',
    title: 'Candidate Profile Review',
    description: 'Human-assisted profile auditing to prevent fake accounts, misleading info, and duplicate profiles.',
    icon: UserCheck,
  },
  {
    id: 'badge-privacy-first',
    title: 'Privacy-First Platform',
    description: 'Designed from the ground up for discreet executive movement without public career exposure.',
    icon: Lock,
  },
  {
    id: 'badge-no-public-db',
    title: 'No Public Candidate Database',
    description: 'Your career profile is never indexable by public web search engines or unverified visitors.',
    icon: Database,
  },
  {
    id: 'badge-no-data-selling',
    title: 'No Data Selling',
    description: 'We generate revenue exclusively through software subscriptions, never by monetizing candidate PII.',
    icon: Ban,
  },
  {
    id: 'badge-no-spam',
    title: 'No Spam Outreach',
    description: 'Algorithmic rate-limiting and activity monitoring prevent high-volume, low-relevance mass blasts.',
    icon: MailCheck,
  },
  {
    id: 'badge-secure-payments',
    title: 'Secure Payments',
    description: 'Encrypted tier checkout via PCI-DSS compliant payment gateways with verified invoices.',
    icon: CreditCard,
  },
  {
    id: 'badge-credit-transparency',
    title: 'Transparent Credit Usage',
    description: 'Clear, predictable credit deductions with zero hidden charges or surprise automatic debits.',
    icon: Coins,
  },
  {
    id: 'badge-protected-contact',
    title: 'Protected Contact Details',
    description: 'Direct email and phone numbers remain masked until an explicit, mutual candidate unlock occurs.',
    icon: EyeOff,
  },
  {
    id: 'badge-account-deletion',
    title: 'Account Deletion Available',
    description: 'Complete user agency with self-service account pause or permanent data purge requests.',
    icon: UserX,
  },
  {
    id: 'badge-security-monitoring',
    title: 'Security Monitoring',
    description: '24/7 automated telemetry protecting system endpoints from unauthorized scraping and abuse.',
    icon: Activity,
  },
  {
    id: 'badge-professional-standards',
    title: 'Professional Community Standards',
    description: 'Strict anti-harassment and anti-fraud enforcement guaranteeing respectful leadership engagement.',
    icon: Award,
  },
];

// Detailed Core Commitments
const commitments: CommitmentSection[] = [
  {
    id: 'no-fake-recruiters',
    title: 'No Fake Recruiters',
    icon: Building2,
    summary: 'Access to candidate profiles is not automatically available to anyone who registers. Recruiters must undergo verification before gaining access to search and outreach tools.',
    points: [
      'Company domain validation & review',
      'Corporate business email verification',
      'Organizational structure review',
      'Identity verification checks where appropriate',
    ],
  },
  {
    id: 'no-fake-candidates',
    title: 'No Fake Candidates',
    icon: UserCheck,
    summary: 'Nexgent is committed to maintaining a high-quality professional network through systematic validation.',
    points: [
      'Reduction of fake and fraudulent profiles',
      'Elimination of duplicate registrations',
      'Filtering out misleading or inflated credentials',
      'Prevention of low-quality submissions',
    ],
  },
  {
    id: 'manual-profile-review',
    title: 'Manual Profile Review',
    icon: Search,
    summary: 'Unlike traditional job portals that instantly publish unverified entries, Nexgent reviews candidate and recruiter accounts prior to granting full access.',
    points: [
      'Elevation of overall network talent quality',
      'Protection of system integrity and reputation',
      'Increased confidence for corporate recruiters',
      'Enhanced trust for discrete executive candidates',
    ],
  },
  {
    id: 'privacy-first-design',
    title: 'Privacy-First by Design',
    icon: Lock,
    summary: 'Privacy is a foundational architecture, not an afterthought. Experienced leaders can explore new career steps discreetly.',
    points: [
      'Controlled candidate profile visibility settings',
      'Tiered access permissions for recruiter accounts',
      'Verified corporate recruiter participation only',
      'Candidate-controlled invitation engagement',
    ],
  },
  {
    id: 'protected-contact-info',
    title: 'Protected Contact Information',
    icon: EyeOff,
    summary: 'Personal contact details (email addresses, phone numbers, personal links) remain shielded from open view and are disclosed strictly through mutual engagement workflows.',
  },
  {
    id: 'no-selling-personal-data',
    title: 'No Selling Personal Data',
    icon: Ban,
    summary: 'Nexgent does not sell personal information to advertisers, data brokers, or third parties. Our business relies solely on subscription services.',
  },
  {
    id: 'no-mass-candidate-harvesting',
    title: 'No Mass Candidate Harvesting',
    icon: Database,
    summary: 'We actively combat bulk profile extraction, automated web scraping, and unauthorized candidate data reselling.',
  },
  {
    id: 'no-spam-invitations',
    title: 'No Spam Invitations',
    icon: MailCheck,
    summary: 'Recruitment interactions must remain targeted. We take action against excessive unsolicited outreach, irrelevant blasts, and abusive communication.',
  },
  {
    id: 'verified-access',
    title: 'Verified Access, Not Open Access',
    icon: ShieldCheck,
    summary: 'Access to executive talent is governed by verification status, active subscription permissions, and candidate-defined privacy preferences.',
  },
  {
    id: 'candidate-choice',
    title: 'Candidate Choice Comes First',
    icon: User,
    summary: 'Candidates maintain full autonomy to respond, accept, decline, or ignore invitations without penalty or forced obligations.',
  },
  {
    id: 'billing-transparency',
    title: 'Transparency in Credits and Billing',
    icon: Coins,
    summary: 'Clear subscription details, transparent credit accounting, defined billing rules, and no hidden fees or unexpected charges.',
  },
  {
    id: 'security-conscious',
    title: 'Security-Conscious Operations',
    icon: Activity,
    summary: 'Encrypted communications, session controls, activity auditing, and continuous infrastructure updates protect network participants.',
  },
  {
    id: 'fair-standards',
    title: 'Fair Platform Standards',
    icon: Scale,
    summary: 'Zero tolerance for fraudulent activity, misrepresentation, harassment, or attempts to bypass platform boundaries.',
  },
  {
    id: 'continuous-improvement',
    title: 'Continuous Improvement',
    icon: RefreshCw,
    summary: 'Trust requires ongoing diligence. We continuously refine verification workflows, privacy features, and platform safeguards.',
  },
];

const principles = [
  { title: 'Privacy', desc: 'Professional opportunities should not require public exposure or personal loss of control.' },
  { title: 'Trust', desc: 'Verification, identity integrity, and operational accountability matter above all else.' },
  { title: 'Quality', desc: 'Curated, high-intent leadership connections are exponentially more valuable than sheer volume.' },
  { title: 'Transparency', desc: 'Platform policies, credit usage, and operational rules must remain simple and explicit.' },
  { title: 'Professionalism', desc: 'Both candidates and recruiters deserve a respectful, high-caliber recruitment environment.' },
];

export const TrustTransparency: React.FC = () => {
  const [activeTab, setActiveTab] = useState<string>('overview');

  const scrollToSection = (id: string) => {
    setActiveTab(id);
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  return (
    <div className="min-h-screen bg-slate-950 text-slate-300 font-sans selection:bg-cyan-500 selection:text-slate-950 relative overflow-hidden">
      <LandingHeader/>
      {/* Background Cyber Glow Effects */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[900px] h-[350px] bg-cyan-500/10 blur-[150px] pointer-events-none rounded-full" />
      <div className="absolute top-1/3 right-0 w-[450px] h-[450px] bg-blue-600/10 blur-[160px] pointer-events-none rounded-full" />

      {/* Header Banner */}
      <header className="border-b border-slate-800 bg-slate-900/60 backdrop-blur-md sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
          <div className="space-y-1">
            <div className="flex items-center gap-2">
              <span className="px-2.5 py-0.5 text-xs font-mono font-medium bg-cyan-950 text-cyan-400 border border-cyan-800/50 rounded-full tracking-wide">
                PLATFORM INTEGRITY & TRUST
              </span>
              <span className="text-xs text-slate-500">Motherson Infotech Division</span>
            </div>
            <h1 className="text-2xl md:text-3xl font-extrabold text-white tracking-tight flex items-center gap-2.5">
              <ShieldCheck className="w-7 h-7 text-cyan-400" />
              Trust & Transparency
            </h1>
          </div>
          <div className="flex items-center gap-3 text-xs font-mono text-slate-400 bg-slate-900/80 px-3 py-2 rounded-lg border border-slate-800">
            <HeartHandshake className="w-4 h-4 text-cyan-400" />
            <span>Verified Leadership Ecosystem</span>
          </div>
        </div>
      </header>

      {/* Hero Introduction */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-10 pb-6 text-center space-y-4">
        <h2 className="text-2xl sm:text-3xl font-bold text-white max-w-3xl mx-auto leading-tight">
          Building a Trusted Leadership Hiring Platform
        </h2>
        <p className="text-slate-400 text-xs sm:text-sm max-w-2xl mx-auto leading-relaxed">
          Nexgent was created with a simple belief: Professional opportunities should be built on trust, privacy, and meaningful connections—not mass outreach, public exposure, or unverified interactions.
        </p>
      </section>

      {/* VISUAL TRUST SIGNALS GRID (12 BADGES / CARDS AT TOP) */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="flex items-center gap-2 mb-4">
          <Sparkles className="w-4 h-4 text-cyan-400" />
          <h3 className="text-xs font-mono text-cyan-400 uppercase font-semibold tracking-wider">
            Nexgent Core Trust Signals
          </h3>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
          {trustSignals.map((signal) => {
            const Icon = signal.icon;
            return (
              <div
                key={signal.id}
                className="p-4 bg-slate-900/70 border border-slate-800 rounded-xl space-y-2 hover:border-cyan-800/60 transition-all hover:bg-slate-900/90 group shadow-lg"
              >
                <div className="flex items-center gap-2.5">
                  <div className="p-2 bg-slate-950 text-cyan-400 rounded-lg border border-slate-800 group-hover:border-cyan-800/80 group-hover:bg-cyan-950/40 transition-colors">
                    <Icon className="w-4 h-4 shrink-0" />
                  </div>
                  <span className="text-xs font-bold text-white tracking-tight">{signal.title}</span>
                </div>
                <p className="text-[11px] text-slate-400 leading-relaxed pl-0.5">
                  {signal.description}
                </p>
              </div>
            );
          })}
        </div>
      </section>

      {/* Main Content Layout */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 grid grid-cols-1 lg:grid-cols-12 gap-8">
        
        {/* Navigation Sidebar */}
        <aside className="lg:col-span-4 xl:col-span-3">
          <div className="sticky top-24 bg-slate-900/70 border border-slate-800 rounded-xl p-4 backdrop-blur-md shadow-xl">
            <h2 className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-3 px-2">
              Trust Index
            </h2>
            <nav className="space-y-1 max-h-[calc(100vh-160px)] overflow-y-auto pr-1">
              {commitments.map((item, idx) => {
                const Icon = item.icon;
                const isActive = activeTab === item.id;
                return (
                  <button
                    key={item.id}
                    onClick={() => scrollToSection(item.id)}
                    className={`w-full flex items-center justify-between px-3 py-2 text-xs font-medium rounded-lg transition-all text-left ${
                      isActive
                        ? 'bg-cyan-950/60 text-cyan-300 border border-cyan-800/60 shadow-[0_0_15px_rgba(6,182,212,0.15)]'
                        : 'text-slate-400 hover:text-slate-200 hover:bg-slate-800/50'
                    }`}
                  >
                    <div className="flex items-center gap-2 truncate">
                      <span className="text-[10px] font-mono text-slate-500 w-4">{idx + 1}.</span>
                      <Icon className={`w-3.5 h-3.5 shrink-0 ${isActive ? 'text-cyan-400' : 'text-slate-500'}`} />
                      <span className="truncate">{item.title}</span>
                    </div>
                    <ChevronRight className={`w-3.5 h-3.5 shrink-0 transition-transform ${isActive ? 'text-cyan-400 translate-x-0.5' : 'text-slate-600'}`} />
                  </button>
                );
              })}
            </nav>
          </div>
        </aside>

        {/* Content Body */}
        <div className="lg:col-span-8 xl:col-span-9 space-y-10">
          
          {/* Detailed Commitments */}
          <div className="space-y-6">
            <h2 className="text-xl font-bold text-white flex items-center gap-2 border-b border-slate-800 pb-3">
              <ShieldCheck className="w-5 h-5 text-cyan-400" />
              Our Platform Commitments
            </h2>

            {commitments.map((c) => {
              const Icon = c.icon;
              return (
                <section
                  key={c.id}
                  id={c.id}
                  className="scroll-mt-28 space-y-3 bg-slate-900/40 p-6 rounded-2xl border border-slate-800/80 hover:border-slate-700 transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <div className="p-2.5 bg-slate-800 text-cyan-400 rounded-lg border border-slate-700">
                      <Icon className="w-5 h-5" />
                    </div>
                    <h3 className="text-lg font-bold text-white">{c.title}</h3>
                  </div>

                  <p className="text-slate-300 text-xs sm:text-sm leading-relaxed">
                    {c.summary}
                  </p>

                  {c.points && c.points.length > 0 && (
                    <div className="pt-2 grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs">
                      {c.points.map((pt, pIdx) => (
                        <div key={pIdx} className="p-2.5 bg-slate-950/80 border border-slate-800/80 rounded-lg flex items-center gap-2 text-slate-300">
                          <CheckCircle2 className="w-3.5 h-3.5 text-cyan-400 shrink-0" />
                          <span className="text-[11px]">{pt}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </section>
              );
            })}
          </div>

          {/* Section: What You Can Expect From Nexgent */}
          <section className="space-y-6 bg-slate-900/40 p-6 sm:p-8 rounded-2xl border border-slate-800/80">
            <h2 className="text-xl font-bold text-white flex items-center gap-2 border-b border-slate-800 pb-3">
              <Sparkles className="w-5 h-5 text-cyan-400" />
              What You Can Expect From Nexgent
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              
              {/* As a Candidate */}
              <div className="p-5 bg-slate-950/80 border border-slate-800 rounded-xl space-y-3">
                <div className="flex items-center gap-2 text-cyan-400 font-semibold text-sm">
                  <User className="w-4 h-4" />
                  <span>As a Candidate</span>
                </div>
                <ul className="space-y-2 text-xs text-slate-300">
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="w-4 h-4 text-cyan-400 shrink-0 mt-0.5" />
                    <span>Privacy-conscious, discreet hiring experience</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="w-4 h-4 text-cyan-400 shrink-0 mt-0.5" />
                    <span>Granular, candidate-controlled professional profile visibility</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="w-4 h-4 text-cyan-400 shrink-0 mt-0.5" />
                    <span>Exclusively verified recruiter and enterprise employer interactions</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="w-4 h-4 text-cyan-400 shrink-0 mt-0.5" />
                    <span>Ironclad protection from mass recruiter outreach and spam</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="w-4 h-4 text-cyan-400 shrink-0 mt-0.5" />
                    <span>Curated leadership opportunities matched precisely to intent</span>
                  </li>
                </ul>
              </div>

              {/* As a Recruiter */}
              <div className="p-5 bg-slate-950/80 border border-slate-800 rounded-xl space-y-3">
                <div className="flex items-center gap-2 text-cyan-400 font-semibold text-sm">
                  <Building2 className="w-4 h-4" />
                  <span>As a Recruiter</span>
                </div>
                <ul className="space-y-2 text-xs text-slate-300">
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="w-4 h-4 text-cyan-400 shrink-0 mt-0.5" />
                    <span>Access to verified, high-caliber leadership candidate profiles</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="w-4 h-4 text-cyan-400 shrink-0 mt-0.5" />
                    <span>Verification-driven platform ecosystem that builds talent response</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="w-4 h-4 text-cyan-400 shrink-0 mt-0.5" />
                    <span>Higher candidate response rates and meaningful executive engagement</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="w-4 h-4 text-cyan-400 shrink-0 mt-0.5" />
                    <span>Structured, transparent credit and hiring invitation workflows</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="w-4 h-4 text-cyan-400 shrink-0 mt-0.5" />
                    <span>A professional, highly controlled recruitment environment</span>
                  </li>
                </ul>
              </div>

            </div>
          </section>

          {/* Section: Our Guiding Principles */}
          <section className="space-y-6 bg-slate-900/40 p-6 sm:p-8 rounded-2xl border border-slate-800/80">
            <h2 className="text-xl font-bold text-white flex items-center gap-2 border-b border-slate-800 pb-3">
              <Award className="w-5 h-5 text-cyan-400" />
              Our Guiding Principles
            </h2>
            <p className="text-xs text-slate-400 leading-relaxed">
              Everything we engineer at Nexgent is governed by five foundational pillars:
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {principles.map((p, idx) => (
                <div key={idx} className="p-4 bg-slate-950 border border-slate-800 rounded-xl space-y-1.5">
                  <div className="text-cyan-400 font-bold text-xs font-mono uppercase flex items-center gap-1.5">
                    <div className="w-1.5 h-1.5 rounded-full bg-cyan-400" />
                    <span>{p.title}</span>
                  </div>
                  <p className="text-[11px] text-slate-300 leading-relaxed">{p.desc}</p>
                </div>
              ))}
            </div>
          </section>

          {/* Section: Our Promise */}
          <section className="p-6 sm:p-8 bg-slate-900/80 border border-cyan-800/50 rounded-2xl shadow-[0_0_35px_rgba(6,182,212,0.1)] space-y-4 text-center">
            <div className="inline-flex p-3 bg-cyan-950 text-cyan-400 rounded-full border border-cyan-800">
              <ShieldCheck className="w-6 h-6" />
            </div>
            <h2 className="text-xl font-bold text-white">Our Solemn Promise</h2>
            <p className="text-xs sm:text-sm text-slate-300 max-w-2xl mx-auto leading-relaxed">
              Nexgent is committed to creating a private, verified, and trustworthy leadership hiring network where senior professionals and recruiters connect with confidence, transparency, and complete respect. That commitment guides how we build our platform, review profiles, protect information, and support our executive community every single day.
            </p>

            <div className="p-3 bg-slate-950/60 rounded-lg border border-slate-800/80 text-center text-xs text-cyan-500 font-mono mt-4">
              {/* © {new Date().getFullYear()} Motherson Infotech / Nexgent Trust & Governance. All rights reserved. */}
              Nexgent Trust & Governance Framework • Verified Profiles • Private Access • Privacy First
            </div>
          </section>

        </div>
      </main>
      <LandingFooter/>
    </div>
  );
};

export default TrustTransparency;